import React, { useState } from "react";
import API from "../services/api";
import { KeyRound, Loader2 } from "lucide-react";

/**
 * The gate that stands between a handed-over password and the rest of the app.
 *
 * Every crew account starts on a password an admin gave them - by the
 * operator's choice, their own first name - and an admin reset puts them back
 * on one. Both are guessable by anyone who has seen a run sheet, so the
 * account cannot reach a single screen until the person picks their own.
 * Nothing here is dismissable: the only ways out are a successful change or
 * signing out.
 */
export default function ChangePassword({ username, onDone, onLogout }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("The two new passwords do not match.");
      return;
    }
    if (next.length < 8) {
      setError("Your new password needs to be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await API.post("/auth/change-password", { current_password: current, new_password: next });
      setCurrent(""); setNext(""); setConfirm("");
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Could not change your password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl">
        <div className="flex flex-col items-center mb-6 text-center">
          <div className="w-14 h-14 bg-amber-500/15 border border-amber-500/30 rounded-2xl flex items-center justify-center mb-4">
            <KeyRound size={24} className="text-amber-400" />
          </div>
          <h1 className="text-xl font-bold text-white">Choose your own password</h1>
          <p className="text-sm text-slate-400 mt-2">
            {username ? <span className="text-slate-300">{username}</span> : "Your account"} is still using
            the password you were given. Pick one only you know before carrying on.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5" htmlFor="cp-current">
              Password you were given
            </label>
            <input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2.5 text-slate-200 outline-none focus:border-blue-500"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5" htmlFor="cp-new">
              New password
            </label>
            <input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2.5 text-slate-200 outline-none focus:border-blue-500"
              required
            />
            <p className="text-[11px] text-slate-500 mt-1.5">
              At least 8 characters. Do not use your name.
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5" htmlFor="cp-confirm">
              New password again
            </label>
            <input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2.5 text-slate-200 outline-none focus:border-blue-500"
              required
            />
          </div>

          {error && (
            <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2 transition-colors"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            {busy ? "Saving" : "Save password"}
          </button>
        </form>

        {onLogout && (
          <button onClick={onLogout} className="w-full text-slate-500 hover:text-slate-300 text-sm mt-5 transition-colors">
            Sign out instead
          </button>
        )}
      </div>
    </div>
  );
}
